import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  AddOnDto,
  AddOnPurchaseIntentRequest,
  AddOnPurchaseIntentResult
} from "@/types/api";

/**
 * Catalogue query — anonymous endpoint, cache for an hour. The optional
 * `scope` filter maps to the backend's `?scope=Container|Account` query.
 */
export function useAddOns(scope?: "Container" | "Account") {
  return useQuery({
    queryKey: ["addons", scope ?? "all"],
    queryFn: ({ signal }) => {
      const path = scope ? `/api/addons?scope=${scope}` : "/api/addons";
      return api.get<AddOnDto[]>(path, signal);
    },
    staleTime: 60 * 60_000
  });
}

/**
 * Build a HostShop redirect for buying the add-on. The backend resolves
 * container ownership server-side for Container-scoped add-ons, so a
 * bogus container_id just yields a 404 — no security held by the client.
 */
export function useCreateAddOnPurchaseIntent(slug: string) {
  return useMutation({
    mutationFn: (body: AddOnPurchaseIntentRequest) =>
      api.post<AddOnPurchaseIntentResult>(
        `/api/addons/${encodeURIComponent(slug)}/purchase-intent`,
        body
      )
  });
}
